import React, { useState, useMemo } from 'react';
import {
  Dialog,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Tabs,
  Tab,
  Card,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip as MuiTooltip,
  alpha,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import {
  ResponsiveContainer,
  ComposedChart,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
} from 'recharts';

import {
  COMPANY,
  FISCAL_YEARS,
  YEAR_META,
  FY24_RESTATEMENTS,
  BALANCE_SHEET_LAYOUT,
  PROFIT_AND_LOSS_LAYOUT,
} from '../constants/sattvaFinancials';
import { buildFinanceAnalytics, RATIO_GROUPS } from '../services/financeAnalytics';

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'profitability', label: 'Profitability' },
  { key: 'balance', label: 'Balance Sheet' },
  { key: 'ratios', label: 'Ratios' },
  { key: 'statements', label: 'Statements' },
];

const PALETTE = ['#4C9AFF', '#57D9A3', '#FFAB4C', '#FF7452', '#B388FF', '#4DD0E1', '#F06292', '#AED581'];

const TONE = { positive: '#57D9A3', negative: '#FF7452', warning: '#FFAB4C', neutral: '#4C9AFF' };

// The app theme sets shape.borderRadius to 25 for pill buttons, and MUI treats
// a numeric sx borderRadius as a multiple of it — panels use explicit px.
const RADIUS = { panel: '12px', inset: '8px' };

// ---------- formatting ----------
// Source figures are INR millions; the unit toggle converts to crore (÷10).

const UNITS = {
  mn: { label: '₹ Mn', factor: 1, suffix: 'Mn' },
  cr: { label: '₹ Cr', factor: 0.1, suffix: 'Cr' },
};

const fmtAmt = (v, unit = 'mn', digits = 0) => {
  if (v === null || v === undefined || isNaN(v)) return '—';
  const u = UNITS[unit];
  const scaled = v * u.factor;
  return `₹${scaled.toLocaleString('en-IN', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })} ${u.suffix}`;
};

const fmtAmtShort = (v, unit = 'mn') => {
  if (v === null || v === undefined || isNaN(v)) return '—';
  const scaled = v * UNITS[unit].factor;
  return scaled.toLocaleString('en-IN', { maximumFractionDigits: 0 });
};

/** Statement-table figure: two decimals, negatives in accounting parentheses. */
const fmtStatement = (v, unit = 'mn') => {
  if (v === null || v === undefined || isNaN(v)) return '—';
  const scaled = Math.abs(v) * UNITS[unit].factor;
  const s = scaled.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return v < 0 ? `(${s})` : s;
};

const fmtPct = (v, digits = 1) =>
  v === null || v === undefined || isNaN(v) ? '—' : `${v.toFixed(digits)}%`;

const fmtX = (v, digits = 2) =>
  v === null || v === undefined || isNaN(v) ? '—' : `${v.toFixed(digits)}x`;

const fmtDays = (v) => (v === null || v === undefined || isNaN(v) ? '—' : `${Math.round(v)}d`);

const fmtRupees = (v) =>
  v === null || v === undefined || isNaN(v)
    ? '—'
    : `₹${v.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const fmtSigned = (v, fmt) => {
  if (v === null || v === undefined || isNaN(v)) return '—';
  return `${v > 0 ? '+' : ''}${fmt(v)}`;
};

const FORMATTERS = {
  pct: (v) => fmtPct(v),
  x: (v) => fmtX(v),
  days: (v) => fmtDays(v),
};

// ---------- building blocks ----------

/**
 * One metric. `hero` doubles the type scale for the handful of numbers that
 * should dominate a section; everything else stays compact so a screen of
 * thirty KPIs still has a visual hierarchy.
 */
const Stat = ({ label, value, sub, delta, deltaTone, tone = 'neutral', hero = false, title }) => {
  const theme = useTheme();
  const accent = TONE[tone] || TONE.neutral;
  const dTone = deltaTone ? TONE[deltaTone] : theme.palette.text.secondary;

  const body = (
    <Card
      elevation={0}
      sx={{
        flex: hero ? '1 1 210px' : '1 1 155px',
        minWidth: hero ? 190 : 145,
        // Without a cap, the last one or two cards in a row stretch across the
        // leftover width and read as a different, more important kind of card.
        maxWidth: hero ? 360 : 300,
        p: hero ? 2.25 : 1.75,
        borderRadius: RADIUS.panel,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.background.paper,
        position: 'relative',
        overflow: 'hidden',
        transition: 'border-color .15s ease',
        '&:hover': { borderColor: alpha(accent, 0.6) },
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 3,
          bgcolor: accent,
        },
      }}
    >
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: 0.6,
          textTransform: 'uppercase',
          color: theme.palette.text.secondary,
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: hero ? 26 : 19,
          fontWeight: 700,
          lineHeight: 1.15,
          wordBreak: 'break-word',
          color: theme.palette.text.primary,
        }}
      >
        {value}
      </Typography>
      {(sub || delta) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.4, flexWrap: 'wrap' }}>
          {delta && (
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: dTone }}>{delta}</Typography>
          )}
          {sub && (
            <Typography sx={{ fontSize: 11.5, color: theme.palette.text.secondary }}>{sub}</Typography>
          )}
        </Box>
      )}
    </Card>
  );

  return title ? (
    <MuiTooltip title={title} arrow>
      {body}
    </MuiTooltip>
  ) : (
    body
  );
};

const Section = ({ title, caption, children }) => {
  const theme = useTheme();
  return (
    <Box sx={{ mb: 3.5 }}>
      {title && (
        <Box sx={{ mb: 1.25 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase' }}>
            {title}
          </Typography>
          {caption && (
            <Typography sx={{ fontSize: 12, color: theme.palette.text.secondary, mt: 0.25 }}>
              {caption}
            </Typography>
          )}
        </Box>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>{children}</Box>
    </Box>
  );
};

const ChartCard = ({ title, caption, children, height = 290, full = false }) => {
  const theme = useTheme();
  return (
    <Card
      elevation={0}
      sx={{
        flex: full ? '1 1 100%' : '1 1 440px',
        minWidth: 300,
        p: 2,
        borderRadius: RADIUS.panel,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.background.paper,
      }}
    >
      <Typography sx={{ fontSize: 14, fontWeight: 700, mb: caption ? 0.25 : 1.5 }}>{title}</Typography>
      {caption && (
        <Typography sx={{ fontSize: 12, color: theme.palette.text.secondary, mb: 1.25 }}>
          {caption}
        </Typography>
      )}
      <Box sx={{ width: '100%', height }}>{children}</Box>
    </Card>
  );
};

const Callout = ({ children, tone = 'warning' }) => {
  const theme = useTheme();
  const accent = TONE[tone];
  return (
    <Box
      sx={{
        mb: 2,
        p: 1.5,
        borderRadius: RADIUS.panel,
        border: `1px solid ${alpha(accent, 0.5)}`,
        bgcolor: alpha(accent, 0.08),
        display: 'flex',
        gap: 1,
        alignItems: 'flex-start',
      }}
    >
      <InfoOutlinedIcon sx={{ fontSize: 18, color: accent, mt: 0.2 }} />
      <Box sx={{ fontSize: 12.5, color: theme.palette.text.secondary, lineHeight: 1.6 }}>
        {children}
      </Box>
    </Box>
  );
};

// ---------- chart helpers ----------

const useAxis = () => {
  const theme = useTheme();
  return {
    tick: { fill: theme.palette.text.secondary, fontSize: 11 },
    stroke: alpha(theme.palette.text.secondary, 0.3),
    tickLine: false,
  };
};

const ChartTip = ({ formatter }) => {
  const theme = useTheme();
  return (
    <Tooltip
      formatter={formatter}
      contentStyle={{
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        fontSize: 12,
        color: theme.palette.text.primary,
      }}
      cursor={{ fill: alpha(theme.palette.text.secondary, 0.08) }}
    />
  );
};

const legendStyle = { fontSize: 11 };

// ---------- tab views ----------

const OverviewTab = ({ a, unit }) => {
  const { bs, pl, derived, cagrs, series, latest, prior } = a;
  const axis = useAxis();
  const money = (v) => fmtAmt(v, unit);
  const tipMoney = (v, name) => [fmtAmt(v, unit), name];

  // Every headline card carries its own YoY so the newest year is never read
  // in isolation.
  const delta = (line, fmt = (v) => fmtPct(v), goodUp = true) => {
    const cur = line[latest];
    const prv = line[prior];
    if (cur === null || prv === null || !prv) return {};
    const g = ((cur - prv) / Math.abs(prv)) * 100;
    return {
      delta: fmtSigned(g, fmt),
      deltaTone: g === 0 ? 'neutral' : (g > 0) === goodUp ? 'positive' : 'negative',
    };
  };

  const latestAssetMix = useMemo(() => {
    const r = series.assetMix[latest];
    return Object.entries(r)
      .filter(([k]) => !['year', 'audited'].includes(k))
      .map(([name, value]) => ({ name, value }))
      .filter((d) => d.value > 0)
      .sort((x, y) => y.value - x.value);
  }, [series.assetMix, latest]);

  return (
    <>
      <Section
        title="Headline results — FY25"
        caption="Unaudited limited-review figures, with year-on-year change against audited FY24."
      >
        <Stat hero label="Revenue from operations" value={money(pl.revenueFromOperations[latest])} {...delta(pl.revenueFromOperations)} sub="YoY" tone="neutral" />
        <Stat hero label="Total income" value={money(pl.totalIncome[latest])} {...delta(pl.totalIncome)} sub="YoY" tone="neutral" />
        <Stat hero label="EBITDA" value={money(derived.ebitda[latest])} {...delta(derived.ebitda)} sub="YoY" tone="positive" title="Profit before tax + finance costs + depreciation" />
        <Stat hero label="Profit after tax" value={money(pl.profitForTheYear[latest])} {...delta(pl.profitForTheYear)} sub="YoY" tone="positive" />
        <Stat hero label="Total assets" value={money(bs.totalAssets[latest])} {...delta(bs.totalAssets)} sub="YoY" tone="neutral" />
        <Stat hero label="Net worth" value={money(bs.totalEquity[latest])} {...delta(bs.totalEquity)} sub="YoY" tone="positive" title="Total equity including non-controlling interest" />
      </Section>

      <Section title="Earnings detail">
        <Stat label="Profit before tax" value={money(pl.profitBeforeTax[latest])} {...delta(pl.profitBeforeTax)} tone="positive" />
        <Stat label="EBIT" value={money(derived.ebit[latest])} {...delta(derived.ebit)} tone="positive" title="Profit before tax + finance costs" />
        <Stat label="Other income" value={money(pl.otherIncome[latest])} {...delta(pl.otherIncome)} tone="warning" title="Non-operating income — a large share of total income here" />
        <Stat label="Share of JVs & associates" value={money(pl.shareOfProfitFromJv[latest])} {...delta(pl.shareOfProfitFromJv)} tone="warning" />
        <Stat label="Own-operations profit" value={money(pl.profitBeforeShareOfJv[latest])} {...delta(pl.profitBeforeShareOfJv)} tone="positive" title="Post-tax profit before equity-accounted JV share" />
        <Stat label="Total comprehensive income" value={money(pl.totalComprehensiveIncome[latest])} {...delta(pl.totalComprehensiveIncome)} tone="positive" />
        <Stat label="Attributable to owners" value={money(pl.tciOwnersOfParent[latest])} {...delta(pl.tciOwnersOfParent)} tone="positive" />
        <Stat label="Earnings per share" value={fmtRupees(pl.earningsPerShare[latest])} {...delta(pl.earningsPerShare)} tone="positive" title="Basic & diluted, as reported" />
      </Section>

      <Section title="Balance sheet scale">
        <Stat label="Capital employed" value={money(derived.capitalEmployed[latest])} {...delta(derived.capitalEmployed)} tone="neutral" title="Total equity + borrowings" />
        <Stat label="Gross borrowings" value={money(derived.totalBorrowings[latest])} {...delta(derived.totalBorrowings, (v) => fmtPct(v), false)} tone="warning" />
        <Stat label="Net debt" value={money(derived.netDebt[latest])} {...delta(derived.netDebt, (v) => fmtPct(v), false)} tone="negative" title="Borrowings less cash, bank balances and current investments" />
        <Stat label="Cash & liquid assets" value={money(derived.liquidAssets[latest])} {...delta(derived.liquidAssets)} tone="positive" />
        <Stat label="Working capital" value={money(derived.workingCapital[latest])} {...delta(derived.workingCapital)} tone="positive" />
        <Stat label="Real estate asset base" value={money(derived.realEstateAssetBase[latest])} {...delta(derived.realEstateAssetBase)} tone="neutral" title="Investment property + property under development + inventories" />
        <Stat label="Inventories" value={money(bs.inventories[latest])} {...delta(bs.inventories)} tone="neutral" />
        <Stat label="Non-current investments" value={money(bs.nonCurrentInvestments[latest])} {...delta(bs.nonCurrentInvestments)} tone="neutral" />
        <Stat label="Trade receivables" value={money(bs.tradeReceivables[latest])} {...delta(bs.tradeReceivables, (v) => fmtPct(v), false)} tone="warning" />
        <Stat label="Trade payables" value={money(derived.tradePayables[latest])} {...delta(derived.tradePayables, (v) => fmtPct(v), false)} tone="warning" />
      </Section>

      <Section title="Key ratios — FY25" caption="Change shown against FY24.">
        <Stat label="EBITDA margin" value={fmtPct(derived.ebitdaMargin[latest])} delta={fmtSigned(derived.ebitdaMargin[latest] - derived.ebitdaMargin[prior], (v) => `${v.toFixed(1)}pp`)} deltaTone={derived.ebitdaMargin[latest] >= derived.ebitdaMargin[prior] ? 'positive' : 'negative'} tone="positive" />
        <Stat label="PAT margin" value={fmtPct(derived.patMargin[latest])} delta={fmtSigned(derived.patMargin[latest] - derived.patMargin[prior], (v) => `${v.toFixed(1)}pp`)} deltaTone={derived.patMargin[latest] >= derived.patMargin[prior] ? 'positive' : 'negative'} tone="positive" />
        <Stat label="Return on equity" value={fmtPct(derived.roe[latest])} delta={fmtSigned(derived.roe[latest] - derived.roe[prior], (v) => `${v.toFixed(1)}pp`)} deltaTone={derived.roe[latest] >= derived.roe[prior] ? 'positive' : 'negative'} tone="positive" title="PAT / average total equity" />
        <Stat label="Return on capital employed" value={fmtPct(derived.roce[latest])} delta={fmtSigned(derived.roce[latest] - derived.roce[prior], (v) => `${v.toFixed(1)}pp`)} deltaTone={derived.roce[latest] >= derived.roce[prior] ? 'positive' : 'negative'} tone="positive" title="EBIT / average capital employed" />
        <Stat label="Debt / equity" value={fmtX(derived.debtToEquity[latest])} delta={fmtSigned(derived.debtToEquity[latest] - derived.debtToEquity[prior], (v) => `${v.toFixed(2)}x`)} deltaTone={derived.debtToEquity[latest] <= derived.debtToEquity[prior] ? 'positive' : 'negative'} tone="warning" />
        <Stat label="Net debt / EBITDA" value={fmtX(derived.netDebtToEbitda[latest])} tone="warning" title="Lower is safer; negative means net cash" />
        <Stat label="Interest coverage" value={fmtX(derived.interestCoverage[latest])} delta={fmtSigned(derived.interestCoverage[latest] - derived.interestCoverage[prior], (v) => `${v.toFixed(2)}x`)} deltaTone={derived.interestCoverage[latest] >= derived.interestCoverage[prior] ? 'positive' : 'negative'} tone="positive" title="EBIT / finance costs" />
        <Stat label="Current ratio" value={fmtX(derived.currentRatio[latest])} delta={fmtSigned(derived.currentRatio[latest] - derived.currentRatio[prior], (v) => `${v.toFixed(2)}x`)} deltaTone={derived.currentRatio[latest] >= derived.currentRatio[prior] ? 'positive' : 'negative'} tone="neutral" />
        <Stat label="Effective tax rate" value={fmtPct(derived.effectiveTaxRate[latest])} tone="neutral" title="Total tax expense / profit before tax" />
        <Stat label="Asset turnover" value={fmtX(derived.assetTurnover[latest])} tone="neutral" title="Revenue / average total assets" />
        <Stat label="JV share of PAT" value={fmtPct(derived.jvContribution[latest])} tone="warning" title="How much of profit comes from equity-accounted JVs rather than consolidated operations" />
        <Stat label="Equity / total assets" value={fmtPct(derived.equityRatio[latest])} tone="positive" />
      </Section>

      <Section title="Three-year compound growth" caption="FY22 to FY25 CAGR.">
        <Stat label="Revenue CAGR" value={fmtPct(cagrs.revenue)} tone="positive" />
        <Stat label="Total income CAGR" value={fmtPct(cagrs.totalIncome)} tone="positive" />
        <Stat label="EBITDA CAGR" value={fmtPct(cagrs.ebitda)} tone="positive" />
        <Stat label="PAT CAGR" value={fmtPct(cagrs.pat)} tone="positive" />
        <Stat label="Total assets CAGR" value={fmtPct(cagrs.totalAssets)} tone="neutral" />
        <Stat label="Net worth CAGR" value={fmtPct(cagrs.equity)} tone="positive" />
        <Stat label="Asset base CAGR" value={fmtPct(cagrs.realEstateAssetBase)} tone="neutral" title="Investment property + under development + inventories" />
        <Stat label="EPS CAGR" value={fmtPct(cagrs.eps)} tone="positive" />
      </Section>

      <Section title="Trends">
        <ChartCard title="Income and profit" caption={`Bars in ${UNITS[unit].label}; PAT margin on the right axis.`}>
          <ResponsiveContainer>
            <ComposedChart data={series.performance}>
              <CartesianGrid strokeDasharray="3 3" stroke={axis.stroke} vertical={false} />
              <XAxis dataKey="year" {...axis} />
              <YAxis yAxisId="l" {...axis} tickFormatter={(v) => fmtAmtShort(v, unit)} />
              <YAxis yAxisId="r" orientation="right" {...axis} tickFormatter={(v) => `${Math.round(v)}%`} />
              <ChartTip formatter={(v, n) => (n === 'PAT margin' ? fmtPct(v) : fmtAmt(v, unit))} />
              <Legend wrapperStyle={legendStyle} />
              <Bar yAxisId="l" dataKey="totalIncome" name="Total income" fill={PALETTE[0]} radius={[4, 4, 0, 0]} />
              <Bar yAxisId="l" dataKey="ebitda" name="EBITDA" fill={PALETTE[1]} radius={[4, 4, 0, 0]} />
              <Bar yAxisId="l" dataKey="pat" name="PAT" fill={PALETTE[2]} radius={[4, 4, 0, 0]} />
              <Line yAxisId="r" type="monotone" dataKey="patMargin" name="PAT margin" stroke={PALETTE[4]} strokeWidth={2} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Balance sheet growth" caption="Assets, net worth and borrowings.">
          <ResponsiveContainer>
            <LineChart data={series.balanceSheetGrowth}>
              <CartesianGrid strokeDasharray="3 3" stroke={axis.stroke} vertical={false} />
              <XAxis dataKey="year" {...axis} />
              <YAxis {...axis} tickFormatter={(v) => fmtAmtShort(v, unit)} />
              <ChartTip formatter={tipMoney} />
              <Legend wrapperStyle={legendStyle} />
              <Line type="monotone" dataKey="totalAssets" name="Total assets" stroke={PALETTE[0]} strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="equity" name="Net worth" stroke={PALETTE[1]} strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="borrowings" name="Borrowings" stroke={PALETTE[3]} strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="realEstateAssetBase" name="Real estate base" stroke={PALETTE[4]} strokeWidth={2} strokeDasharray="4 3" dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="How the balance sheet is funded" caption="Equity versus borrowed and operating liabilities.">
          <ResponsiveContainer>
            <BarChart data={series.fundingMix}>
              <CartesianGrid strokeDasharray="3 3" stroke={axis.stroke} vertical={false} />
              <XAxis dataKey="year" {...axis} />
              <YAxis {...axis} tickFormatter={(v) => fmtAmtShort(v, unit)} />
              <ChartTip formatter={tipMoney} />
              <Legend wrapperStyle={legendStyle} />
              {['Equity', 'Borrowings', 'Trade payables', 'Other liabilities'].map((k, i) => (
                <Bar key={k} dataKey={k} stackId="f" fill={PALETTE[i]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title={`Where the assets sit — ${FISCAL_YEARS[latest]}`} caption="Share of total assets.">
          <ResponsiveContainer>
            <PieChart>
              <Pie
                data={latestAssetMix}
                dataKey="value"
                nameKey="name"
                innerRadius={55}
                outerRadius={95}
                paddingAngle={2}
              >
                {latestAssetMix.map((_, i) => (
                  <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                ))}
              </Pie>
              <ChartTip formatter={tipMoney} />
              <Legend wrapperStyle={legendStyle} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </Section>
    </>
  );
};

const ProfitabilityTab = ({ a, unit }) => {
  const { pl, derived, series, latest } = a;
  const axis = useAxis();
  const money = (v) => fmtAmt(v, unit);
  const tipMoney = (v, n) => [fmtAmt(v, unit), n];

  return (
    <>
      <Callout tone="neutral">
        Margins here are struck on <strong>total income</strong>, not revenue from operations. Other
        income runs at {fmtPct(derived.otherIncomeShare[latest])} of total income in{' '}
        {FISCAL_YEARS[latest]}, so a revenue-only denominator would flatter every margin on this page.
        Note that PAT margin can exceed 100%: profit includes the equity-accounted share of
        associates and JVs, which never appears in total income. FY23 is the extreme case at{' '}
        {fmtPct(derived.patMargin[1])}.
      </Callout>

      <Section title="Profit and loss — FY25">
        <Stat hero label="Total income" value={money(pl.totalIncome[latest])} tone="neutral" />
        <Stat hero label="EBITDA" value={money(derived.ebitda[latest])} sub={fmtPct(derived.ebitdaMargin[latest]) + ' margin'} tone="positive" />
        <Stat hero label="Profit before tax" value={money(pl.profitBeforeTax[latest])} sub={fmtPct(derived.pbtMargin[latest]) + ' margin'} tone="positive" />
        <Stat hero label="Profit after tax" value={money(pl.profitForTheYear[latest])} sub={fmtPct(derived.patMargin[latest]) + ' margin'} tone="positive" />
        <Stat label="Gross profit" value={money(derived.grossProfit[latest])} sub={fmtPct(derived.grossMargin[latest]) + ' of revenue'} tone="positive" title="Revenue less project development, traded stock, stores and inventory movement" />
        <Stat label="Core operating profit" value={money(derived.coreOperatingProfit[latest])} tone="warning" title="EBITDA excluding other income — what the development business earns on its own" />
        <Stat label="Total expenses" value={money(pl.totalExpenses[latest])} tone="negative" />
        <Stat label="Finance costs" value={money(pl.financeCosts[latest])} sub={fmtPct(derived.financeCostRatio[latest]) + ' of income'} tone="negative" />
        <Stat label="Employee cost" value={money(pl.employeeBenefitsExpenses[latest])} sub={fmtPct(derived.employeeCostRatio[latest]) + ' of income'} tone="neutral" />
        <Stat label="Depreciation" value={money(pl.depreciationAndAmortization[latest])} tone="neutral" />
        <Stat label="Tax expense" value={money(derived.totalTax[latest])} sub={fmtPct(derived.effectiveTaxRate[latest]) + ' effective'} tone="neutral" />
        <Stat label="EPS" value={fmtRupees(pl.earningsPerShare[latest])} tone="positive" />
      </Section>

      <Section title="Composition and mix">
        <ChartCard title="Income split" caption="Operating revenue versus other income; the line is other income's share.">
          <ResponsiveContainer>
            <ComposedChart data={series.incomeSplit}>
              <CartesianGrid strokeDasharray="3 3" stroke={axis.stroke} vertical={false} />
              <XAxis dataKey="year" {...axis} />
              <YAxis yAxisId="l" {...axis} tickFormatter={(v) => fmtAmtShort(v, unit)} />
              <YAxis yAxisId="r" orientation="right" {...axis} tickFormatter={(v) => `${Math.round(v)}%`} />
              <ChartTip formatter={(v, n) => (n === 'Other income share' ? fmtPct(v) : fmtAmt(v, unit))} />
              <Legend wrapperStyle={legendStyle} />
              <Bar yAxisId="l" dataKey="Revenue from operations" stackId="i" fill={PALETTE[0]} />
              <Bar yAxisId="l" dataKey="Other income" stackId="i" fill={PALETTE[2]} />
              <Line yAxisId="r" type="monotone" dataKey="otherIncomeShare" name="Other income share" stroke={PALETTE[4]} strokeWidth={2} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Expense stack"
          caption="Negative inventory movement means unsold stock was capitalised, reducing the charge."
        >
          <ResponsiveContainer>
            <BarChart data={series.expenseMix}>
              <CartesianGrid strokeDasharray="3 3" stroke={axis.stroke} vertical={false} />
              <XAxis dataKey="year" {...axis} />
              <YAxis {...axis} tickFormatter={(v) => fmtAmtShort(v, unit)} />
              <ReferenceLine y={0} stroke={axis.stroke} />
              <ChartTip formatter={tipMoney} />
              <Legend wrapperStyle={legendStyle} />
              {['Project development', 'Stock & stores', 'Inventory movement', 'Employee', 'Finance', 'Depreciation', 'Other'].map(
                (k, i) => (
                  <Bar key={k} dataKey={k} stackId="e" fill={PALETTE[i % PALETTE.length]} />
                )
              )}
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Margin trend" caption="All struck on total income.">
          <ResponsiveContainer>
            <LineChart data={series.margins}>
              <CartesianGrid strokeDasharray="3 3" stroke={axis.stroke} vertical={false} />
              <XAxis dataKey="year" {...axis} />
              <YAxis {...axis} tickFormatter={(v) => `${Math.round(v)}%`} />
              <ChartTip formatter={(v, n) => [fmtPct(v), n]} />
              <Legend wrapperStyle={legendStyle} />
              <Line type="monotone" dataKey="ebitdaMargin" name="EBITDA margin" stroke={PALETTE[1]} strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="ebitMargin" name="EBIT margin" stroke={PALETTE[0]} strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="pbtMargin" name="PBT margin" stroke={PALETTE[4]} strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="patMargin" name="PAT margin" stroke={PALETTE[2]} strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="effectiveTaxRate" name="Effective tax rate" stroke={PALETTE[3]} strokeWidth={2} strokeDasharray="4 3" dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Where profit comes from"
          caption="Consolidated own operations versus the equity-accounted share of JVs and associates."
        >
          <ResponsiveContainer>
            <ComposedChart data={series.profitBridge}>
              <CartesianGrid strokeDasharray="3 3" stroke={axis.stroke} vertical={false} />
              <XAxis dataKey="year" {...axis} />
              <YAxis yAxisId="l" {...axis} tickFormatter={(v) => fmtAmtShort(v, unit)} />
              <YAxis yAxisId="r" orientation="right" {...axis} tickFormatter={(v) => `${Math.round(v)}%`} />
              <ChartTip formatter={(v, n) => (n === 'JV share of PAT' ? fmtPct(v) : fmtAmt(v, unit))} />
              <Legend wrapperStyle={legendStyle} />
              <Bar yAxisId="l" dataKey="Own operations (post-tax)" stackId="p" fill={PALETTE[0]} />
              <Bar yAxisId="l" dataKey="Associates & JVs" stackId="p" fill={PALETTE[2]} />
              <Line yAxisId="r" type="monotone" dataKey="jvContribution" name="JV share of PAT" stroke={PALETTE[3]} strokeWidth={2} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Earnings per share" caption="Basic and diluted, as reported (₹).">
          <ResponsiveContainer>
            <BarChart data={series.perShare}>
              <CartesianGrid strokeDasharray="3 3" stroke={axis.stroke} vertical={false} />
              <XAxis dataKey="year" {...axis} />
              <YAxis {...axis} tickFormatter={(v) => v.toLocaleString('en-IN')} />
              <ChartTip formatter={(v, n) => [fmtRupees(v), n]} />
              <Bar dataKey="eps" name="EPS" fill={PALETTE[1]} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Cost intensity" caption="Key expense lines as a share of total income.">
          <ResponsiveContainer>
            <LineChart
              data={FISCAL_YEARS.map((year, i) => ({
                year,
                'Finance cost': derived.financeCostRatio[i],
                'Employee cost': derived.employeeCostRatio[i],
                'Other expenses': derived.otherExpenseRatio[i],
              }))}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={axis.stroke} vertical={false} />
              <XAxis dataKey="year" {...axis} />
              <YAxis {...axis} tickFormatter={(v) => `${Math.round(v)}%`} />
              <ChartTip formatter={(v, n) => [fmtPct(v), n]} />
              <Legend wrapperStyle={legendStyle} />
              <Line type="monotone" dataKey="Finance cost" stroke={PALETTE[3]} strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="Employee cost" stroke={PALETTE[0]} strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="Other expenses" stroke={PALETTE[4]} strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </Section>
    </>
  );
};

const BalanceSheetTab = ({ a, unit }) => {
  const { bs, derived, series, latest } = a;
  const axis = useAxis();
  const money = (v) => fmtAmt(v, unit);
  const tipMoney = (v, n) => [fmtAmt(v, unit), n];

  return (
    <>
      <Section title="Position at FY25 close">
        <Stat hero label="Total assets" value={money(bs.totalAssets[latest])} tone="neutral" />
        <Stat hero label="Net worth" value={money(bs.totalEquity[latest])} sub={fmtPct(derived.equityRatio[latest]) + ' of assets'} tone="positive" />
        <Stat hero label="Gross borrowings" value={money(derived.totalBorrowings[latest])} sub={fmtX(derived.debtToEquity[latest]) + ' of equity'} tone="warning" />
        <Stat hero label="Net debt" value={money(derived.netDebt[latest])} sub={fmtX(derived.netDebtToEbitda[latest]) + ' of EBITDA'} tone="negative" />
        <Stat label="Non-current assets" value={money(bs.totalNonCurrentAssets[latest])} sub={fmtPct(derived.nonCurrentAssetShare[latest])} tone="neutral" />
        <Stat label="Current assets" value={money(bs.totalCurrentAssets[latest])} tone="neutral" />
        <Stat label="Current liabilities" value={money(bs.totalCurrentLiabilities[latest])} tone="warning" />
        <Stat label="Working capital" value={money(derived.workingCapital[latest])} sub={fmtPct(derived.workingCapitalToAssets[latest]) + ' of assets'} tone="positive" />
        <Stat label="Total liabilities" value={money(derived.totalLiabilities[latest])} sub={fmtX(derived.liabilitiesToEquity[latest]) + ' of equity'} tone="negative" />
        <Stat label="Capital employed" value={money(derived.capitalEmployed[latest])} tone="neutral" />
        <Stat label="Lease liabilities" value={money(derived.leaseLiabilities[latest])} tone="neutral" />
        <Stat label="Goodwill" value={money(bs.goodwill[latest])} sub={fmtPct(derived.goodwillToEquity[latest]) + ' of net worth'} tone="warning" />
      </Section>

      <Section title="Asset base composition">
        <Stat label="Investment property" value={money(bs.investmentProperty[latest])} tone="neutral" />
        <Stat label="Under development" value={money(bs.investmentPropertyUnderDev[latest])} tone="neutral" />
        <Stat label="Inventories" value={money(bs.inventories[latest])} sub={fmtPct(derived.inventoryShare[latest]) + ' of assets'} tone="neutral" />
        <Stat label="Real estate asset base" value={money(derived.realEstateAssetBase[latest])} sub={fmtPct(derived.realEstateShare[latest]) + ' of assets'} tone="positive" />
        <Stat label="Non-current investments" value={money(bs.nonCurrentInvestments[latest])} sub={fmtPct(derived.investmentShare[latest]) + ' of assets'} tone="warning" title="Largely the equity-accounted JV and associate holdings" />
        <Stat label="Loans given" value={money(bs.loans[latest])} tone="neutral" />
        <Stat label="Other financial assets" value={money(bs.otherCurrentFinancialAssets[latest])} tone="neutral" />
        <Stat label="Cash & bank" value={money(bs.cashAndCashEquivalents[latest] + bs.otherBankBalances[latest])} tone="positive" />
      </Section>

      <Section title="Structure over time">
        <ChartCard title="Asset composition" caption="Stacked by category." height={320}>
          <ResponsiveContainer>
            <BarChart data={series.assetMix}>
              <CartesianGrid strokeDasharray="3 3" stroke={axis.stroke} vertical={false} />
              <XAxis dataKey="year" {...axis} />
              <YAxis {...axis} tickFormatter={(v) => fmtAmtShort(v, unit)} />
              <ChartTip formatter={tipMoney} />
              <Legend wrapperStyle={legendStyle} />
              {['Investment property', 'Property under development', 'Inventories', 'Investments', 'Receivables', 'Cash & bank', 'Loans given', 'Other assets'].map(
                (k, i) => (
                  <Bar key={k} dataKey={k} stackId="a" fill={PALETTE[i % PALETTE.length]} />
                )
              )}
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Leverage" caption="Debt levels against the coverage they support." height={320}>
          <ResponsiveContainer>
            <ComposedChart data={series.leverage}>
              <CartesianGrid strokeDasharray="3 3" stroke={axis.stroke} vertical={false} />
              <XAxis dataKey="year" {...axis} />
              <YAxis yAxisId="l" {...axis} tickFormatter={(v) => fmtAmtShort(v, unit)} />
              <YAxis yAxisId="r" orientation="right" {...axis} tickFormatter={(v) => `${v.toFixed(1)}x`} />
              <ChartTip formatter={(v, n) => (n.includes('/') || n.includes('coverage') ? fmtX(v) : fmtAmt(v, unit))} />
              <Legend wrapperStyle={legendStyle} />
              <Bar yAxisId="l" dataKey="totalBorrowings" name="Borrowings" fill={PALETTE[3]} radius={[4, 4, 0, 0]} />
              <Bar yAxisId="l" dataKey="equity" name="Net worth" fill={PALETTE[1]} radius={[4, 4, 0, 0]} />
              <Line yAxisId="r" type="monotone" dataKey="debtToEquity" name="Debt / equity" stroke={PALETTE[0]} strokeWidth={2} dot={{ r: 3 }} />
              <Line yAxisId="r" type="monotone" dataKey="interestCoverage" name="Interest coverage" stroke={PALETTE[4]} strokeWidth={2} strokeDasharray="4 3" dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Liquidity" caption="Coverage of current liabilities.">
          <ResponsiveContainer>
            <ComposedChart data={series.liquidity}>
              <CartesianGrid strokeDasharray="3 3" stroke={axis.stroke} vertical={false} />
              <XAxis dataKey="year" {...axis} />
              <YAxis yAxisId="l" {...axis} tickFormatter={(v) => fmtAmtShort(v, unit)} />
              <YAxis yAxisId="r" orientation="right" {...axis} tickFormatter={(v) => `${v.toFixed(1)}x`} />
              <ChartTip formatter={(v, n) => (n === 'Working capital' ? fmtAmt(v, unit) : fmtX(v))} />
              <Legend wrapperStyle={legendStyle} />
              <Bar yAxisId="l" dataKey="workingCapital" name="Working capital" fill={PALETTE[0]} radius={[4, 4, 0, 0]} />
              <Line yAxisId="r" type="monotone" dataKey="currentRatio" name="Current ratio" stroke={PALETTE[1]} strokeWidth={2} dot={{ r: 3 }} />
              <Line yAxisId="r" type="monotone" dataKey="quickRatio" name="Quick ratio" stroke={PALETTE[2]} strokeWidth={2} dot={{ r: 3 }} />
              <Line yAxisId="r" type="monotone" dataKey="cashRatio" name="Cash ratio" stroke={PALETTE[3]} strokeWidth={2} strokeDasharray="4 3" dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Working capital cycle" caption="Days. Inventory days dominate for a developer.">
          <ResponsiveContainer>
            <ComposedChart data={series.workingCapitalCycle}>
              <CartesianGrid strokeDasharray="3 3" stroke={axis.stroke} vertical={false} />
              <XAxis dataKey="year" {...axis} />
              <YAxis {...axis} tickFormatter={(v) => Math.round(v)} />
              <ChartTip formatter={(v, n) => [fmtDays(v), n]} />
              <Legend wrapperStyle={legendStyle} />
              <Bar dataKey="inventoryDays" name="Inventory days" fill={PALETTE[0]} radius={[4, 4, 0, 0]} />
              <Bar dataKey="receivableDays" name="Receivable days" fill={PALETTE[1]} radius={[4, 4, 0, 0]} />
              <Bar dataKey="payableDays" name="Payable days" fill={PALETTE[2]} radius={[4, 4, 0, 0]} />
              <Line type="monotone" dataKey="cashConversionCycle" name="Cash conversion cycle" stroke={PALETTE[3]} strokeWidth={2} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>
      </Section>
    </>
  );
};

const RatiosTab = ({ a }) => {
  const theme = useTheme();
  const { derived, series } = a;
  const axis = useAxis();

  return (
    <>
      <Callout tone="neutral">
        Ratios that mix a flow with a balance (returns, turnover, days) use average opening and
        closing balances. FY22 has no opening balance in this dataset, so its figures fall back to
        closing balances and are not strictly comparable with later years.
      </Callout>

      <Section title="Trend charts">
        <ChartCard title="Returns" caption="On average balances.">
          <ResponsiveContainer>
            <LineChart data={series.returns}>
              <CartesianGrid strokeDasharray="3 3" stroke={axis.stroke} vertical={false} />
              <XAxis dataKey="year" {...axis} />
              <YAxis {...axis} tickFormatter={(v) => `${Math.round(v)}%`} />
              <ChartTip formatter={(v, n) => [fmtPct(v), n]} />
              <Legend wrapperStyle={legendStyle} />
              <Line type="monotone" dataKey="roe" name="Return on equity" stroke={PALETTE[1]} strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="roce" name="Return on capital employed" stroke={PALETTE[0]} strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="roa" name="Return on assets" stroke={PALETTE[2]} strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Solvency" caption="Gearing and debt service capacity.">
          <ResponsiveContainer>
            <LineChart data={series.leverage}>
              <CartesianGrid strokeDasharray="3 3" stroke={axis.stroke} vertical={false} />
              <XAxis dataKey="year" {...axis} />
              <YAxis {...axis} tickFormatter={(v) => `${v.toFixed(1)}x`} />
              <ReferenceLine y={0} stroke={axis.stroke} />
              <ChartTip formatter={(v, n) => [fmtX(v), n]} />
              <Legend wrapperStyle={legendStyle} />
              <Line type="monotone" dataKey="debtToEquity" name="Debt / equity" stroke={PALETTE[3]} strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="netDebtToEquity" name="Net debt / equity" stroke={PALETTE[2]} strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="netDebtToEbitda" name="Net debt / EBITDA" stroke={PALETTE[4]} strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="interestCoverage" name="Interest coverage" stroke={PALETTE[1]} strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </Section>

      <Section title="Full ratio grid" caption="Every metric, every year. Arrow shows the FY24 to FY25 direction.">
        <Card
          elevation={0}
          sx={{
            flex: '1 1 100%',
            p: 2,
            borderRadius: RADIUS.panel,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.background.paper,
            overflowX: 'auto',
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, fontSize: 12, color: theme.palette.text.secondary }}>
                  Metric
                </TableCell>
                {FISCAL_YEARS.map((y) => (
                  <TableCell
                    key={y}
                    align="right"
                    sx={{ fontWeight: 700, fontSize: 12, color: theme.palette.text.secondary, whiteSpace: 'nowrap' }}
                  >
                    {y}
                    {!YEAR_META[y].audited && ' *'}
                  </TableCell>
                ))}
                <TableCell align="center" sx={{ fontWeight: 700, fontSize: 12, color: theme.palette.text.secondary }}>
                  Trend
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {RATIO_GROUPS.map((g) => (
                <React.Fragment key={g.group}>
                  <TableRow>
                    <TableCell
                      colSpan={FISCAL_YEARS.length + 2}
                      sx={{
                        fontWeight: 700,
                        fontSize: 11.5,
                        letterSpacing: 0.6,
                        textTransform: 'uppercase',
                        color: theme.palette.text.secondary,
                        bgcolor: alpha(theme.palette.text.secondary, 0.05),
                        borderBottom: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      {g.group}
                    </TableCell>
                  </TableRow>
                  {g.rows.map((r) => {
                    const line = derived[r.key] || [];
                    const fmt = FORMATTERS[r.format] || ((v) => v);
                    const cur = line[line.length - 1];
                    const prv = line[line.length - 2];
                    let arrow = null;
                    if (cur !== null && prv !== null && cur !== undefined && prv !== undefined) {
                      const up = cur > prv;
                      const flat = Math.abs(cur - prv) < 1e-9;
                      const good = r.good === 'flat' ? null : (up ? r.good === 'up' : r.good === 'down');
                      const color =
                        good === null
                          ? theme.palette.text.secondary
                          : good
                          ? TONE.positive
                          : TONE.negative;
                      arrow = flat ? null : up ? (
                        <ArrowUpwardIcon sx={{ fontSize: 15, color }} />
                      ) : (
                        <ArrowDownwardIcon sx={{ fontSize: 15, color }} />
                      );
                    }
                    return (
                      <TableRow key={r.key} hover>
                        <TableCell sx={{ fontSize: 13, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}>
                          {r.label}
                        </TableCell>
                        {FISCAL_YEARS.map((y, i) => (
                          <TableCell
                            key={y}
                            align="right"
                            sx={{
                              fontSize: 13,
                              fontVariantNumeric: 'tabular-nums',
                              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                            }}
                          >
                            {line[i] === null || line[i] === undefined ? '—' : fmt(line[i])}
                          </TableCell>
                        ))}
                        <TableCell align="center" sx={{ borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}>
                          {arrow}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
          <Typography sx={{ fontSize: 11.5, color: theme.palette.text.secondary, mt: 1.5 }}>
            * FY25 is unaudited (limited review). Arrow colour reflects whether the FY24 to FY25 move
            is favourable for that metric; neutral-direction metrics are shown in grey.
          </Typography>
        </Card>
      </Section>
    </>
  );
};

const StatementRow = ({ row, values, unit, theme }) => {
  if (row.type === 'header') {
    return (
      <TableRow>
        <TableCell
          colSpan={FISCAL_YEARS.length + 1}
          sx={{
            fontWeight: 700,
            fontSize: 11.5,
            letterSpacing: 0.6,
            textTransform: 'uppercase',
            color: theme.palette.text.secondary,
            bgcolor: alpha(theme.palette.text.secondary, 0.05),
            pl: 1 + (row.depth || 0) * 1.5,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          {row.label}
        </TableCell>
      </TableRow>
    );
  }

  const line = values[row.key] || [];
  const border = row.grand
    ? `2px solid ${theme.palette.divider}`
    : `1px solid ${alpha(theme.palette.divider, 0.5)}`;

  return (
    <TableRow hover>
      <TableCell
        sx={{
          fontSize: 13,
          fontWeight: row.total ? 700 : 400,
          pl: 1 + (row.depth || 0) * 1.5,
          borderBottom: border,
        }}
      >
        {row.label}
      </TableCell>
      {FISCAL_YEARS.map((y, i) => (
        <TableCell
          key={y}
          align="right"
          sx={{
            fontSize: 13,
            fontWeight: row.total ? 700 : 400,
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
            borderBottom: border,
          }}
        >
          {row.raw ? fmtRupees(line[i]) : fmtStatement(line[i], unit)}
        </TableCell>
      ))}
    </TableRow>
  );
};

const StatementTable = ({ title, caption, layout, values, unit }) => {
  const theme = useTheme();
  return (
    <Card
      elevation={0}
      sx={{
        flex: '1 1 100%',
        p: 2,
        borderRadius: RADIUS.panel,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.background.paper,
        overflowX: 'auto',
      }}
    >
      <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{title}</Typography>
      <Typography sx={{ fontSize: 12, color: theme.palette.text.secondary, mb: 1.25 }}>
        {caption}
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, fontSize: 12, color: theme.palette.text.secondary }}>
              Particulars
            </TableCell>
            {FISCAL_YEARS.map((y) => (
              <TableCell
                key={y}
                align="right"
                sx={{ fontWeight: 700, fontSize: 12, color: theme.palette.text.secondary, whiteSpace: 'nowrap' }}
              >
                {y}
                <Typography component="span" sx={{ display: 'block', fontSize: 10, fontWeight: 500 }}>
                  {YEAR_META[y].audited ? 'audited' : 'unaudited'}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {layout.map((row, i) => (
            <StatementRow key={row.key || `h${i}`} row={row} values={values} unit={unit} theme={theme} />
          ))}
        </TableBody>
      </Table>
    </Card>
  );
};

const StatementsTab = ({ a, unit }) => {
  const theme = useTheme();
  const { bs, pl } = a;

  return (
    <>
      <Callout tone="warning">
        FY22–FY24 come from the audited consolidated Ind AS statements; FY25 from the unaudited
        limited-review condensed statements. The limited-review pack restates a few FY24 comparatives
        — this dashboard uses the original audited FY24 figures throughout, and lists the differences
        below.
      </Callout>

      <Section>
        <StatementTable
          title="Consolidated balance sheet"
          caption={`As at March 31, in ${UNITS[unit].label}.`}
          layout={BALANCE_SHEET_LAYOUT}
          values={bs}
          unit={unit}
        />
      </Section>

      <Section>
        <StatementTable
          title="Consolidated statement of profit and loss"
          caption={`For the year ended March 31, in ${UNITS[unit].label}. EPS is in rupees.`}
          layout={PROFIT_AND_LOSS_LAYOUT}
          values={pl}
          unit={unit}
        />
      </Section>

      <Section title="FY24 comparative differences" caption="Audited FY24 statements versus the FY24 comparative column in the limited-review pack.">
        <Card
          elevation={0}
          sx={{
            flex: '1 1 100%',
            p: 2,
            borderRadius: RADIUS.panel,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.background.paper,
            overflowX: 'auto',
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Line item', 'Audited FY24', 'Restated FY24', 'Difference'].map((h, i) => (
                  <TableCell
                    key={h}
                    align={i ? 'right' : 'left'}
                    sx={{ fontWeight: 700, fontSize: 12, color: theme.palette.text.secondary }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {FY24_RESTATEMENTS.map((r) => {
                const diff = r.restated - r.audited;
                return (
                  <TableRow key={r.line} hover>
                    <TableCell sx={{ fontSize: 13 }}>{r.line}</TableCell>
                    <TableCell align="right" sx={{ fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
                      {r.audited.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
                      {r.restated.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontSize: 13,
                        fontVariantNumeric: 'tabular-nums',
                        color: diff === 0 ? theme.palette.text.secondary : diff > 0 ? TONE.positive : TONE.negative,
                      }}
                    >
                      {diff > 0 ? '+' : ''}
                      {diff.toFixed(2)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <Typography sx={{ fontSize: 11.5, color: theme.palette.text.secondary, mt: 1.5 }}>
            All amounts in INR million. Largest single difference is 2.66 million on property, plant
            and equipment — immaterial against a 38,686 million balance sheet.
          </Typography>
        </Card>
      </Section>

      <Section title="Sources">
        <Card
          elevation={0}
          sx={{
            flex: '1 1 100%',
            p: 2,
            borderRadius: RADIUS.panel,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.background.paper,
          }}
        >
          {FISCAL_YEARS.map((y) => (
            <Box key={y} sx={{ display: 'flex', gap: 1.5, alignItems: 'center', py: 0.6 }}>
              <Chip
                size="small"
                label={y}
                sx={{ fontWeight: 700, minWidth: 56, bgcolor: alpha(TONE.neutral, 0.15) }}
              />
              <Typography sx={{ fontSize: 13 }}>
                Year ended {YEAR_META[y].endedOn} — {YEAR_META[y].source}
              </Typography>
              <Chip
                size="small"
                variant="outlined"
                label={YEAR_META[y].audited ? 'Audited' : 'Unaudited'}
                sx={{
                  fontSize: 11,
                  borderColor: YEAR_META[y].audited ? TONE.positive : TONE.warning,
                  color: YEAR_META[y].audited ? TONE.positive : TONE.warning,
                }}
              />
            </Box>
          ))}
          <Typography sx={{ fontSize: 12, color: theme.palette.text.secondary, mt: 1.5 }}>
            {COMPANY.name} · CIN {COMPANY.cin} · {COMPANY.basis} · figures as printed, in{' '}
            {COMPANY.units}. Ratios and roll-ups are derived, not extracted.
          </Typography>
        </Card>
      </Section>
    </>
  );
};

const TAB_VIEWS = {
  overview: OverviewTab,
  profitability: ProfitabilityTab,
  balance: BalanceSheetTab,
  ratios: RatiosTab,
  statements: StatementsTab,
};

// ---------- shell ----------

const FinanceDashboard = ({ open, onClose }) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState('overview');
  const [unit, setUnit] = useState('mn');

  // The source data is a static constant, so this is a pure derivation with no
  // fetch, no loading state and no failure mode.
  const analytics = useMemo(() => buildFinanceAnalytics(), []);

  const ActiveView = TAB_VIEWS[activeTab];

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { bgcolor: theme.palette.background.default } }}
    >
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', minHeight: { xs: 52, sm: 60 }, gap: 1 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: 17, fontWeight: 700, lineHeight: 1.2 }}>
              {COMPANY.name}
            </Typography>
            <Typography noWrap sx={{ fontSize: 11.5, color: theme.palette.text.secondary }}>
              {COMPANY.basis} · FY22–FY25 · FY25 unaudited (limited review)
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={unit}
              onChange={(_, v) => v && setUnit(v)}
              sx={{
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  fontSize: 12,
                  fontWeight: 600,
                  px: 1.25,
                  py: 0.25,
                  borderRadius: '999px !important',
                  border: `1px solid ${theme.palette.divider} !important`,
                  color: theme.palette.text.secondary,
                },
                '& .Mui-selected': { color: theme.palette.text.primary },
              }}
            >
              <ToggleButton value="mn">₹ Mn</ToggleButton>
              <ToggleButton value="cr">₹ Cr</ToggleButton>
            </ToggleButtonGroup>
            <IconButton onClick={onClose} sx={{ color: theme.palette.text.primary }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Toolbar>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ px: 2, borderTop: `1px solid ${theme.palette.divider}`, minHeight: 44 }}
        >
          {TABS.map((t) => (
            <Tab
              key={t.key}
              value={t.key}
              label={t.label}
              sx={{ fontWeight: 600, textTransform: 'none', fontSize: 13.5, minHeight: 44 }}
            />
          ))}
        </Tabs>
      </AppBar>

      <Box sx={{ p: { xs: 1.5, sm: 2.5 }, overflowY: 'auto' }}>
        <ActiveView a={analytics} unit={unit} />
      </Box>
    </Dialog>
  );
};

export default FinanceDashboard;
